import HistoryIcon from '@/assets/images/History.svg';
import Modal from '@/components/Modal';
import { startCase } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Close, Grid, Heading, Image, Spinner } from 'theme-ui';
import {
  AuditLog,
  AuditLogCausedBy,
  useAuditLogsForObjectQuery,
} from '../../graphql/types/generated';
import DataTable, { DataTableColumn } from '../DataTable';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';
import { useTabActions } from '../Tabs';
import {
  AuditLogFields,
  AuditLogTypes,
  baseAuditField,
  createAuditCellRenderer,
  defaultCursor,
  defaultPageInfo,
  defaultRenders,
  formatAuditUser,
  hideDuplicateAmountRows,
  NavigationCursor,
  normalizePageInfo,
  PAGE_SIZE,
  PaginationControls,
  QueryErrorNotice,
} from './utils';

export function AuditLogPane({
  excludeObjectTypes,
  hideDuplicateAmounts = false,
  itemId,
  objects,
  onClose,
  visible,
  types = {},
  fields = {},
  hiddenFields,
  sourceLabels,
  standalone = true,
  title = 'Item Details History',
}: AuditLogPaneParams) {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [cursor, setCursor] = useState<NavigationCursor>(defaultCursor);
  const [pageInfo, setPageInfo] = useState(defaultPageInfo);
  const [hasError, setHasError] = useState(false);
  const endDate = useMemo(() => new Date().toISOString(), []);
  const columns = useMemo(updateColumns, [fields, types]);
  const variables = useMemo(updateCursor, [
    cursor,
    endDate,
    excludeObjectTypes,
    itemId,
    objects,
  ]);
  const [{ data, error, fetching }] = useAuditLogsForObjectQuery({
    variables,
    requestPolicy: 'network-only',
    pause: !visible,
  });

  useEffect(onDataChange, [data, error]);
  useEffect(resetOnClose, [visible]);
  // A non-standalone instance shares a Modal its parent owns, so the parent locks scroll instead.
  useLockBodyScroll(standalone && visible);

  /** Update the view's data when graphql responds */
  function onDataChange() {
    setHasError(!!error);
    const response = data?.viewer?.auditLogs;
    if (error || !response) return;

    const logs = response.edges
      .map((o) => o?.node)
      .filter(Boolean) as AuditLog[];
    const filteredLogs = hiddenFields
      ? logs.filter((log) => !hiddenFields.has(baseAuditField(log.field)))
      : logs;
    setAuditLogs(
      hideDuplicateAmounts
        ? hideDuplicateAmountRows(filteredLogs)
        : filteredLogs,
    );
    setPageInfo(normalizePageInfo(response.pageInfo));
  }

  /** Reset to page 0 on close so reopening doesn't briefly show stale data. */
  function resetOnClose() {
    if (visible) return;
    setAuditLogs([]);
    setCursor(defaultCursor);
    setPageInfo(defaultPageInfo);
    setHasError(false);
  }

  /** Adjust column definitions when either field types or the field mapping changes */
  function updateColumns(): DataTableColumn<AuditLog>[] {
    const allTypes = { ...defaultRenders, ...types };
    return [
      {
        header: 'Time',
        id: 'time',
        accessorFn: (row) => allTypes.date(row.date, row),
        meta: { width: '5%' },
      },
      {
        header: 'Field',
        id: 'field',
        accessorFn: (row) => {
          const base = baseAuditField(row.field);
          const render = fields[base]?.label;
          if (!render) return startCase(base.split('.').pop());

          return typeof render === 'function'
            ? render(row.field, row)
            : allTypes.string(render, row);
        },
        meta: { width: '15%' },
      },
      {
        header: 'New Value',
        id: 'newValue',
        accessorFn: createAuditCellRenderer(fields, allTypes, 'newValue'),
        meta: { width: '35%' },
      },
      {
        header: 'Old Value',
        id: 'oldValue',
        accessorFn: createAuditCellRenderer(fields, allTypes, 'oldValue'),
        meta: { width: '35%' },
      },
      {
        header: 'User',
        id: 'user',
        accessorFn: (row) => formatAuditUser(row.user),
        meta: { width: '5%' },
      },
      {
        header: 'Source',
        id: 'source',
        accessorFn: (col) =>
          sourceLabels?.[col.causedBy] ??
          defaultSourceLabels[col.causedBy] ??
          startCase(col.causedBy),
        meta: { width: '5%' },
      },
    ];
  }

  /** Update the computed cursor when the user defined one changes, or objects */
  function updateCursor() {
    return { ...cursor, endDate, excludeObjectTypes, itemId, objects };
  }

  useTabActions(
    standalone ? null : (
      <PaginationControls
        fetching={fetching}
        pageInfo={pageInfo}
        onPrevious={() =>
          setCursor({ before: pageInfo.startCursor, last: PAGE_SIZE })
        }
        onNext={() =>
          setCursor({ after: pageInfo.endCursor, first: PAGE_SIZE })
        }
      />
    ),
  );

  if (!standalone) {
    return (
      <Box>
        {hasError && <QueryErrorNotice />}
        <Box sx={{ overflow: 'auto', height: 'calc(100vh - 10em)' }}>
          <DataTable columns={columns} data={auditLogs} />
        </Box>
      </Box>
    );
  }

  return (
    <Modal
      contentStyle={{
        borderRadius: 0,
        boxSizing: 'border-box',
        height: '100vh',
        marginRight: 0,
        maxWidth: '70vw',
        whiteSpace: 'nowrap',
      }}
      parent="#root"
      visible={visible}
    >
      <Grid
        columns={2}
        sx={{
          alignItems: 'center',
          gridTemplateColumns: 'auto 1fr auto auto auto auto',
          marginBottom: '2',
        }}
      >
        <Image src={HistoryIcon} />
        <Heading as="h3" margin={0}>
          {title}
        </Heading>
        <Box>{fetching && <Spinner sx={{ height: '30px' }} />}</Box>
        <Button
          disabled={fetching || !pageInfo.hasPreviousPage}
          variant="primary"
          onClick={() =>
            setCursor({ before: pageInfo.startCursor, last: PAGE_SIZE })
          }
        >
          Previous
        </Button>
        <Button
          disabled={fetching || !pageInfo.hasNextPage}
          variant="primary"
          onClick={() =>
            setCursor({ after: pageInfo.endCursor, first: PAGE_SIZE })
          }
        >
          Next
        </Button>
        <Close sx={{ variant: 'buttons.close' }} onClick={onClose} />
      </Grid>
      {hasError ? <QueryErrorNotice /> : null}
      <Box
        sx={{
          overflow: 'auto',
          height: 'calc(100vh - 7em)',
        }}
      >
        <DataTable columns={columns} data={auditLogs} />
      </Box>
    </Modal>
  );
}

const defaultSourceLabels: { [key in AuditLogCausedBy]: string } = {
  GRAPHQL: 'Service',
  JOB: 'Migration',
  KAFKA: 'Service',
  USER: 'Item Page',
  USER_IMPORT: 'Import',
};

export type AuditLogObject = {
  /** The id of the audit. Typically the object id, but it may be a uuid generated by the object's composite pk */
  id: string;
  /** The type of the object, e.g. 'inventoryitem'. */
  type: string;
};
export type AuditLogPaneParams = {
  /** Object types to leave out of the itemId-driven result, e.g. `locationitem` */
  excludeObjectTypes?: string[];

  /** Drop a `*_amountInCents` row when its `*_amount` twin came from the same write. */
  hideDuplicateAmounts?: boolean;

  /** Field to field type renderer map */
  fields?: AuditLogFields;

  /** Set of field names to exclude from the displayed rows */
  hiddenFields?: Set<string>;

  /** Every row for this item, including rows for children that no longer exist, unioned with `objects` */
  itemId?: string;

  /** Various objects to gather audit log history for */
  objects: AuditLogObject[];

  /** Handler for when the close button is pressed */
  onClose: () => void;

  /** Override labels for the Source column by AuditLogCausedBy key */
  sourceLabels?: Partial<{ [key in AuditLogCausedBy]: string }>;

  /** Render its own Modal shell (default). Pass `false` when a parent already owns the Modal, e.g. one tab of a shared pane. */
  standalone?: boolean;

  /** Modal heading text */
  title?: string;

  /** Field type renderers */
  types?: AuditLogTypes;

  /** Flag to show the AuditLogPane */
  visible: boolean;
};
